import { useQuery } from '@tanstack/react-query';
import { type HierarchyLevel, hierarchyLevelsForRoute, settingsRouteDomains } from '@/apps/settings/lib/config-hierarchy';
import { useHub } from '@/contexts/HubContext';
import { getRuleFragment, listFragmentsByScope } from '@/services/config';
import { Scope } from '@/types';

export interface HierarchyLevelState extends HierarchyLevel {
  /** True when this level has its own settings for the page; null when unselected or unable to override. */
  customised: boolean | null;
}

/**
 * The operator → client → debt type chain for the current settings page, with
 * whether each selected level has its own fragment in any of the page's domains.
 */
export function useConfigHierarchy(pathname: string) {
  const { selectedOperator, selectedClient, selectedDebtType } = useHub();
  const levels = hierarchyLevelsForRoute(pathname, selectedOperator, selectedClient, selectedDebtType);
  const domains = settingsRouteDomains(pathname);

  return useQuery({
    queryKey: ['config-hierarchy', pathname, ...levels.map((l) => l.id)],
    enabled: selectedOperator !== null,
    staleTime: 0,
    queryFn: async (): Promise<HierarchyLevelState[]> =>
      Promise.all(
        levels.map(async (level) => {
          if (!level.id || !level.applies || domains.length === 0) return { ...level, customised: null };
          const found = await Promise.all(
            domains.map(async (domain) =>
              level.scope === Scope.OPERATOR
                ? (await listFragmentsByScope(domain, Scope.OPERATOR)).length > 0
                : (await getRuleFragment(domain, level.scope, level.id ?? undefined)) !== null
            )
          );
          return { ...level, customised: found.some(Boolean) };
        })
      ),
  });
}
